import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';

import {
  DeliveryRouteCandidatesDialogData,
  DeliveryRouteCandidatesGroup,
} from '../../interfaces/dialog-data.interface';
import {
  BlockingMaterial,
  DELIVERY_WINDOW_SOURCE_LABEL_KEYS,
  DeliveryRouteTrip,
  EffectiveWindow,
} from '../../interfaces/delivery-route.interface';

// sin uso desde que la ventana horaria particiona en vez de descartar; se conserva
// como base de las futuras restricciones de venta
@Component({
  selector: 'app-delivery-route-candidates-modal',
  standalone: true,
  imports: [
    TranslatePipe,
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatRadioModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './delivery-route-candidates-modal.component.html',
})
export class DeliveryRouteCandidatesModalComponent implements OnInit, OnDestroy {
  // ruta elegida en cada documento, por índice de documento
  selectedByDocument: Record<number, string | null> = {};

  // en pantallas de poca altura (móvil horizontal) el encabezado no deja ver las candidatas
  shortScreen = false;
  private shortScreenSub?: Subscription;

  constructor(
    private dialogRef: MatDialogRef<DeliveryRouteCandidatesModalComponent>,
    private breakpointObserver: BreakpointObserver,
    @Inject(MAT_DIALOG_DATA) public data: DeliveryRouteCandidatesDialogData
  ) {
    for (const document of data.documents ?? []) {
      this.selectedByDocument[document.index] = document.selectedRouteId;
    }
  }

  ngOnInit(): void {
    this.shortScreenSub = this.breakpointObserver
      .observe('(max-height: 560px)')
      .subscribe((state) => (this.shortScreen = state.matches));
  }

  ngOnDestroy(): void {
    this.shortScreenSub?.unsubscribe();
  }

  selectedIndex = 0;

  get groups(): DeliveryRouteCandidatesGroup[] {
    return this.data.documents ?? [];
  }

  // sin partición el documento no se nombra: la presentación queda como sin reparto
  get showsDocumentLabels(): boolean {
    return this.groups.length > 1;
  }

  get selected(): DeliveryRouteCandidatesGroup | null {
    return this.groups[this.selectedIndex] ?? null;
  }

  select(index: number): void {
    this.selectedIndex = index;
  }

  // ruta elegida en ese documento, para resumirla en la lista lateral
  chosenRouteName(group: DeliveryRouteCandidatesGroup): string {
    const routeId = this.selectedByDocument[group.index];
    return group.candidates.find((candidate) => candidate._id === routeId)?.name ?? '';
  }

  get hasAnyCandidate(): boolean {
    return this.groups.some((group) => group.candidates.length > 0);
  }

  get documentsWithoutRoute(): DeliveryRouteCandidatesGroup[] {
    return this.groups.filter((group) => !group.candidates.length);
  }

  get canConfirm(): boolean {
    return !this.documentsWithoutRoute.length || this.data.canGenerateWithoutRoute;
  }

  // consultar el reparto solo tiene sentido cuando hay más de un documento
  get canShowSplit(): boolean {
    return !!this.data.onShowSplit && this.showsDocumentLabels;
  }

  showSplit(): void {
    this.data.onShowSplit?.(this.selectedByDocument);
  }

  private readonly i18n = inject(LanguageService);

  // The backend normalises weekdays to 0..6 with Sunday at 0, so the index IS the key suffix.
  // Resolved per call rather than held in a frozen array: a module-level lookup would capture
  // whatever language was active when the class loaded and never follow a language switch.
  private weekdayShort(day: number): string {
    return this.i18n.resolve(`DOCUMENT_GENERATE.DELIVERY_ROUTE_MODAL.WEEKDAY_SHORT_${day}`);
  }

  private weekdayLong(day: number): string {
    return this.i18n.resolve(`DOCUMENT_GENERATE.DELIVERY_ROUTE_MODAL.WEEKDAY_LONG_${day}`);
  }

  tripWeekday(trip: DeliveryRouteTrip): string {
    if (!trip?.dateHourStartTeoric) return '';
    return this.weekdayLong(new Date(trip.dateHourStartTeoric).getDay());
  }

  effectiveDays(window: EffectiveWindow): string {
    return (window.days ?? [])
      .slice()
      .sort((a, b) => a - b)
      .map((day) => this.weekdayLong(day) || day)
      .join(', ');
  }

  restrictedByNames(window: EffectiveWindow): string {
    return (window.restrictedBy ?? []).map((material) => material.name).join(', ');
  }

  holidayList(dates: string[]): string {
    return (dates ?? [])
      .map((date) => {
        const [year, month, day] = date.slice(0, 10).split('-');
        return `${day}/${month}/${year}`;
      })
      .join(', ');
  }

  requirementNames(material: BlockingMaterial): string {
    return (material.missingRequirements ?? []).map((req) => req.name).join(', ');
  }

  weekdayNames(material: BlockingMaterial): string {
    return (material.days ?? [])
      .slice()
      .sort((a, b) => a - b)
      .map((day) => this.weekdayShort(day) || day)
      .join(', ');
  }

  windowSourceLabel(material: BlockingMaterial): string {
    return material.windowSource
      ? this.i18n.resolve(DELIVERY_WINDOW_SOURCE_LABEL_KEYS[material.windowSource])
      : '-';
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  confirm(): void {
    if (!this.canConfirm) return;
    this.dialogRef.close({ routes: this.selectedByDocument });
  }
}
